import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { db } from "../../firebase";
import { collection, doc, getDoc, addDoc, setDoc } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";

type Mode = "existing" | "create";

// Hard-code your available collections here:
const COLLECTION_OPTIONS = ["users", "devices", "forms", "groups", "recoveryPlans", "sequences", "urls", "user_schedules_2"] as const;

const ControlPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const TIMESTAMP_FIELDS = new Set(["birthday"]);

  // collection selector
  const [selectedCollection, setSelectedCollection] = useState<(typeof COLLECTION_OPTIONS)[number]>("users");

  // modes & status
  const [mode, setMode] = useState<Mode>("existing");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // IDs
  const [sourceId, setSourceId] = useState(""); // for loading existing
  const [loadedDocId, setLoadedDocId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState(""); // always required for saving

  // JSON editor
  const [jsonText, setJsonText] = useState("{\n  \n}");

  // esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const resetState = useCallback(() => {
    setMode("existing");
    setLoading(false);
    setStatus(null);
    setError(null);
    setSourceId("");
    setLoadedDocId(null);
    setTargetId("");
    setJsonText("{\n  \n}");
  }, []);

  const openPanel = () => {
    setIsOpen(true);
    resetState();
  };

  // When collection changes, clear doc-specific state so you don’t mix collections
  useEffect(() => {
    setStatus(null);
    setError(null);
    setSourceId("");
    setLoadedDocId(null);
    setTargetId("");
    setJsonText("{\n  \n}");
  }, [selectedCollection]);

  function toEditable(value: any): any {
    if (value instanceof Timestamp) {
      return value.toDate().toISOString();
    }
    if (Array.isArray(value)) return value.map(toEditable);
    if (value && typeof value === "object") {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) out[k] = toEditable(v);
      return out;
    }
    return value;
  }

  /** Recursively walk an object; for keys in TIMESTAMP_FIELDS, coerce to Firestore Timestamp */
  function fromEditable(value: any, keyHint?: string): any {
    if (Array.isArray(value)) return value.map((v) => fromEditable(v, keyHint));

    if (value && typeof value === "object") {
      // Handle { seconds, nanoseconds } pasted in manually
      if (
        "seconds" in value &&
        "nanoseconds" in value &&
        typeof (value as any).seconds === "number" &&
        typeof (value as any).nanoseconds === "number"
      ) {
        return new Timestamp((value as any).seconds, (value as any).nanoseconds);
      }
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) out[k] = fromEditable(v, k);
      return out;
    }

    // Convert strings/numbers back to Timestamp for targeted keys
    if (keyHint && TIMESTAMP_FIELDS.has(keyHint)) {
      if (typeof value === "number") {
        // Treat as ms since epoch (common in apps)
        return Timestamp.fromDate(new Date(value));
      }
      if (typeof value === "string") {
        // ISO like "2000-01-20" or "2000-01-20T00:00:00Z"
        const d = new Date(value);
        if (!isNaN(d.getTime())) return Timestamp.fromDate(d);
      }
    }

    return value;
  }

  const parseJsonOrSetError = (): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError('JSON must be an object (e.g., { "key": "value" }).');
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch (e: any) {
      setError(`Invalid JSON: ${e.message}`);
      return null;
    }
  };

  // loadExistingDoc
  const loadExistingDoc = async () => {
    setStatus(null);
    setError(null);

    const id = sourceId.trim();
    if (!id) {
      setError("Enter a document ID to load.");
      return;
    }

    setLoading(true);
    try {
      const ref = doc(db, selectedCollection, id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setLoadedDocId(null);
        setTargetId((t) => t || id);
        setJsonText("{\n  \n}");
        setStatus(null);
        setError(`No document found with ID "${id}" in '${selectedCollection}'.`);
        return;
      }

      const editable = toEditable(snap.data());
      setJsonText(JSON.stringify(editable, null, 2));
      setLoadedDocId(snap.id);
      setTargetId((t) => t || snap.id);
      setStatus(`Loaded document ${snap.id} from '${selectedCollection}'.`);
    } catch (e: any) {
      setError(e.message ?? "Failed to load document.");
    } finally {
      setLoading(false);
    }
  };

  // saveToTargetId
  const saveToTargetId = async () => {
    setStatus(null);
    setError(null);

    const id = targetId.trim();
    if (!id) {
      setError("Enter a 'Save To ID' to create/update.");
      return;
    }

    const parsed = parseJsonOrSetError();
    if (!parsed) return;

    const dataToSave = fromEditable(parsed);

    setLoading(true);
    try {
      await setDoc(doc(db, selectedCollection, id), dataToSave, { merge: mode === "existing" });
      setStatus(
        loadedDocId && loadedDocId !== id
          ? `Saved as new document "${id}" in '${selectedCollection}'.`
          : `Saved document "${id}" in '${selectedCollection}'.`
      );
      setLoadedDocId(id);
    } catch (e: any) {
      setError(e.message ?? "Failed to save document.");
    } finally {
      setLoading(false);
    }
  };

  // createWithAutoId
  const createWithAutoId = async () => {
    setStatus(null);
    setError(null);

    const parsed = parseJsonOrSetError();
    if (!parsed) return;

    const dataToSave = fromEditable(parsed);

    setLoading(true);
    try {
      const colRef = collection(db, selectedCollection);
      const newRef = await addDoc(colRef, dataToSave);
      setStatus(`Created with auto ID "${newRef.id}" in '${selectedCollection}'.`);
      setLoadedDocId(newRef.id);
      setTargetId((t) => t || newRef.id);
      setMode("existing");
    } catch (e: any) {
      setError(e.message ?? "Failed to create with auto ID.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <li>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            openPanel();
          }}
        >
          Control Panel
        </a>
      </li>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
            <div className="relative bg-white p-6 md:p-8 rounded-3xl shadow-2xl w-11/12 lg:w-3/4 max-h-[85vh] overflow-auto">
              <h2 className="text-2xl font-bold mb-4">Docs Editor</h2>

              <button
                className="btn btn-sm btn-circle absolute right-4 top-4"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>

              {/* Collection selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Collection</label>
                <select
                  className="select select-bordered w-full max-w-xs"
                  value={selectedCollection}
                  onChange={(e) => setSelectedCollection(e.target.value as (typeof COLLECTION_OPTIONS)[number])}
                >
                  {COLLECTION_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Switching collections clears the current editor and IDs.
                </p>
              </div>

              {/* Mode */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  className={`px-3 py-2 rounded-xl border ${mode === "existing" ? "bg-gray-900 text-white" : "bg-white"}`}
                  onClick={() => setMode("existing")}
                >
                  Edit / Save As
                </button>
                <button
                  className={`px-3 py-2 rounded-xl border ${mode === "create" ? "bg-gray-900 text-white" : "bg-white"}`}
                  onClick={() => setMode("create")}
                >
                  Create
                </button>
              </div>

              {/* Load From ID (source) */}
              <div className="mb-6 space-y-2">
                <label className="block text-sm font-medium">Load From ID (optional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder={`Enter an existing doc ID in '${selectedCollection}'`}
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                  />
                  <button className="btn" onClick={loadExistingDoc} disabled={loading}>
                    {loading ? "Loading..." : "Load"}
                  </button>
                </div>
                {loadedDocId && (
                  <p className="text-xs text-gray-500">
                    Loaded: <span className="font-mono">{loadedDocId}</span>
                  </p>
                )}
              </div>

              {/* Save To ID (target) */}
              <div className="mb-6 space-y-2">
                <label className="block text-sm font-medium">Save To ID (required for create/update)</label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder={`Exact document ID in '${selectedCollection}'`}
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  If different from the loaded ID, this will “Save As” a new document.
                </p>
              </div>

              {/* JSON editor */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Document JSON</label>
                <textarea
                  className="textarea textarea-bordered w-full font-mono text-sm min-h-[240px]"
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  spellCheck={false}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  className="btn btn-primary"
                  onClick={saveToTargetId}
                  disabled={loading}
                >
                  {loading ? "Saving..." : mode === "create" ? "Create / Save To ID" : "Save / Save As"}
                </button>

                {mode === "create" && (
                  <button className="btn" onClick={createWithAutoId} disabled={loading}>
                    {loading ? "Creating..." : "Create With Auto ID"}
                  </button>
                )}

                {status && <span className="text-green-600 text-sm">{status}</span>}
                {error && <span className="text-red-600 text-sm">{error}</span>}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ControlPanel;